import { createFileRoute, Link, Outlet, useMatchRoute } from '@tanstack/react-router';
import { Suspense, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  ExternalLink,
  Eye,
  MapPin,
  Pencil,
  Wallet,
} from 'lucide-react';
import { format } from 'date-fns';

import { useJob, useUpdateJob } from '@/hooks/useJobs';
import { jobsApi } from '@/lib/api/jobs.api';
import { formatSalary } from '@/lib/formatSalary';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { JobStatusUpdater } from '@/components/jobs/JobStatusUpdater';
import { JOB_TYPE_LABELS, WORK_MODE_LABELS, type JobStatus } from '@repo/types';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/jobs/$id')({
  component: JobLayout,
});

const TABS = [
  { label: 'Details', to: '/admin/jobs/$id/details' },
  { label: 'Assigned Customers', to: '/admin/jobs/$id/assigned-customers' },
  { label: 'Related Candidates', to: '/admin/jobs/$id/candidates' },
  { label: 'Scraping', to: '/admin/jobs/$id/scraping' },
  { label: 'Duplicates', to: '/admin/jobs/$id/duplicates' },
  { label: 'Quality', to: '/admin/jobs/$id/quality' },
] as const;

function initialsOf(name?: string) {
  if (!name) return 'JD';
  return (
    name
      .split(/\s+/)
      .map((n) => n[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'JD'
  );
}

function JobLayout() {
  const { id } = Route.useParams();
  const { data: job, isLoading, error } = useJob(id);
  const updateJobMutation = useUpdateJob();
  const matchRoute = useMatchRoute();

  const { data: assignedCustomers = [] } = useQuery({
    queryKey: ['job', id, 'assigned-customers'],
    queryFn: () => jobsApi.getAssignedCustomers(id),
    enabled: !!job,
  });

  const handleStatusChange = useCallback(
    async (newStatus: JobStatus) => {
      try {
        await updateJobMutation.mutateAsync({ id, data: { status: newStatus } });
        toast.success(`Job status updated to ${newStatus}`);
      } catch {
        toast.error('Failed to update job status');
      }
    },
    [id, updateJobMutation]
  );

  const isOnEdit = Boolean(matchRoute({ to: '/admin/jobs/$id/edit', params: { id } }));

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-10 w-full max-w-lg" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="space-y-6 p-6">
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load job details. Please try again.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const salary = formatSalary(job.salaryMin, job.salaryMax, job.salaryCurrency);

  const facts = [
    job.company && { icon: Building2, value: job.company },
    job.location && { icon: MapPin, value: job.location },
    job.jobType && { icon: Briefcase, value: JOB_TYPE_LABELS[job.jobType] ?? job.jobType },
    salary && { icon: Wallet, value: salary },
    {
      icon: Calendar,
      // Prefer the portal's original posting date; fall back to the scrape date.
      value: `Posted ${format(new Date(job.postedDate ?? job.createdAt), 'MMM d, yyyy')}`,
    },
    { icon: Eye, value: `${(job.viewCount ?? 0).toLocaleString()} views` },
  ].filter(Boolean) as { icon: typeof Building2; value: string }[];

  return (
    <div className="space-y-6 p-6 duration-300 animate-in fade-in">
      {/* Top bar: back + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild className="-ml-2 text-muted-foreground">
          <Link to="/admin/jobs">
            <ArrowLeft className="size-4" />
            Back to Jobs
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Suspense fallback={<Skeleton className="h-8 w-28" />}>
            <JobStatusUpdater
              currentStatus={job.status}
              onStatusChange={handleStatusChange}
              isUpdating={updateJobMutation.isPending}
            />
          </Suspense>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/jobs/$id/edit" params={{ id }}>
              <Pencil className="size-4" />
              Edit
            </Link>
          </Button>
          {job.source && (
            <Button variant="outline" size="sm" asChild>
              <a href={job.source} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-4" />
                Original
              </a>
            </Button>
          )}
          {job.applicationUrl && (
            <Button size="sm" asChild>
              <a href={job.applicationUrl} target="_blank" rel="noopener noreferrer">
                Apply
                <ExternalLink className="size-4" />
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Header card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <span className="flex size-16 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xl font-semibold text-primary">
              {initialsOf(job.company)}
            </span>
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl font-semibold tracking-tight">{job.title}</h1>
                {job.workMode && (
                  <Badge variant="secondary" className="capitalize">
                    {WORK_MODE_LABELS[job.workMode] ?? job.workMode}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
                {facts.map((fact, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5">
                    <fact.icon className="size-4 shrink-0" />
                    <span className="text-foreground/90">{fact.value}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab navigation */}
      {!isOnEdit && (
        <div className="flex items-center gap-1 overflow-x-auto border-b">
          {TABS.map((tab) => {
            const active = Boolean(matchRoute({ to: tab.to, params: { id } }));
            const showBadge =
              tab.to === '/admin/jobs/$id/assigned-customers' && assignedCustomers.length > 0;
            return (
              <Link
                key={tab.to}
                to={tab.to}
                params={{ id }}
                className={cn(
                  'relative flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
                  active && 'text-foreground'
                )}
              >
                {tab.label}
                {showBadge && (
                  <Badge variant="secondary" className="h-5 min-w-5 justify-center px-1 text-xs">
                    {assignedCustomers.length}
                  </Badge>
                )}
                {active && (
                  <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            );
          })}
        </div>
      )}

      <Outlet />
    </div>
  );
}
